import {
  ApiRequestError,
  type CatalogueItem,
  type DeliveryWindow,
  type EndpointResponse,
  type guest,
} from '@croissants/contracts';
import type { BasketState } from '../state/basket';

/**
 * Which delivery window the review screen may print (PRD GST-002, GST-021).
 *
 * The draft holds only the start instant of the window the guest picked, and an
 * instant is not something a guest can read: "2026-08-10T05:30:00.000Z" is not a
 * promise, it is an identifier that leaked. So the screen renders a range, and
 * this is where the range comes from.
 *
 * Two sources can supply one, in this order:
 *  1. the quote, which is the server's answer for exactly this basket and
 *     morning, and the one that will be charged;
 *  2. the picker's list for that morning, which is already in the cache from the
 *     screen before and is exact for the start the draft holds. It stands in
 *     while the quote is in flight, and it survives a quote that fails.
 *
 * Null when neither can name it - the picker's answer is not loaded, or it no
 * longer offers this start. The caller must then say what it does know (the
 * start time) rather than invent an end: window length is the platform's to
 * configure (RTE-028), never a screen's to assume.
 */
export function resolvePromisedWindow(
  quoted: DeliveryWindow | undefined,
  offered: EndpointResponse<typeof guest.listWindows> | undefined,
  serviceDay: string | null,
  windowStart: string | null,
): DeliveryWindow | null {
  if (quoted !== undefined) return quoted;
  if (offered === undefined || serviceDay === null || windowStart === null) return null;
  const day = offered.serviceDays.find((entry) => entry.serviceDay === serviceDay);
  return day?.windows.find((offer) => offer.window.start === windowStart)?.window ?? null;
}

/**
 * Where the picker hands the guest on to (PRD GST-002, GST-030).
 *
 * The picker sits in two places - first in mode A, after the brand's vitrine in
 * mode B - but what follows it is the same in both: the counter the guest was
 * reading, or the basket they were filling. It is also the edit step, reached
 * from the basket and the review, and that is a different destination: the
 * screen that opened it says so in `?next=`, and this reads it.
 *
 * With nothing said, a guest who already has a basket goes back to it rather than
 * to the top of the journey - they are editing a choice, not making one, and
 * dropping them at the vitrine is how a basket stops being looked at.
 *
 * Only an in-app path is honoured. `next` arrives from the address bar, and a
 * value that is not a single-slashed local path is discarded rather than
 * followed - this app never turns a URL parameter into an origin.
 */
export function destinationAfterSchedule(search: string, hasBasket: boolean): string {
  const next = new URLSearchParams(search).get('next');
  if (next?.startsWith('/') && !next.startsWith('//')) return next;
  return hasBasket ? '/basket' : '/menu';
}

/**
 * The chosen window stopped fitting (PRD GST-002, RTE-008).
 *
 * A window is offered against the capacity and the preparation time of the
 * moment it was offered, and both move while the guest fills a basket: a big
 * order takes longer to make, another guest takes the last slot, and 22:00
 * passes. The server answers each of those with its own code, and all three mean
 * the same thing to this guest - the half-hour they picked is no longer one they
 * can have - so all three lead to the same screen, which names the nearest ones
 * that still work rather than a retry that cannot succeed.
 *
 * `window_passed` is the fourth, and it belongs here for exactly the same
 * reason (RTE-034): an order for today is confirmed against a re-check that the
 * chosen window is still ahead of accept plus prepare plus travel, and a guest
 * who took two minutes over the payment sheet can slip behind it. Nothing was
 * charged, and the answer is the same one - choose again, from the nearest
 * times that still work.
 */
export function isWindowConflict(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) return false;
  return (
    error.code === 'window_unavailable' ||
    error.code === 'capacity_exhausted' ||
    error.code === 'window_passed'
  );
}

/**
 * Whether this order is for TODAY, and so an immediate one (PRD GST-016).
 *
 * The guest never states a mode and no screen decides one: the day states it,
 * and which day is today is the server's reading of the platform clock, carried
 * on the picker's own answer as `kind`. Asking the browser instead would call a
 * morning "today" that the platform had already moved past (TST-003).
 *
 * False while the picker's answer is not loaded - a screen that cannot tell
 * says the ordinary thing rather than guessing the exceptional one.
 */
export function isTodayOrder(
  offered: EndpointResponse<typeof guest.listWindows> | undefined,
  serviceDay: string | null,
): boolean {
  if (offered === undefined || serviceDay === null) return false;
  return offered.serviceDays.some((day) => day.serviceDay === serviceDay && day.kind === 'TODAY');
}

/**
 * The alternatives to offer when the chosen window stopped fitting, from
 * whichever source can name them (PRD GST-002, RTE-008).
 *
 * Two sources, in the same order and for the same reason as
 * `resolvePromisedWindow` above:
 *  1. the QUOTE's own `alternativeWindows` - computed from the routing answer
 *     that decided the window no longer fits, for exactly this basket and this
 *     bakery, so a replacement taken from it is one checkout will accept;
 *  2. the picker's list, when the refusal came from checkout rather than the
 *     quote and there is no fresher answer to read.
 *
 * One derivation, so the panel can never offer a window from one source beside a
 * verdict from the other.
 */
export function conflictAlternatives(
  quoted: EndpointResponse<typeof guest.quote> | undefined,
  offered: EndpointResponse<typeof guest.listWindows> | undefined,
  windowStart: string | null,
  limit = 3,
): readonly DeliveryWindow[] {
  if (quoted !== undefined && quoted.alternativeWindows.length > 0) {
    return quoted.alternativeWindows.slice(0, limit);
  }
  return nearestWindows(offered, windowStart, limit);
}

/**
 * The selectable windows closest in time to the one that stopped working.
 *
 * Nearest by start instant across every day still orderable, not merely the rest
 * of the chosen morning: when a morning has run out entirely, the honest nearest
 * alternative is tomorrow's earliest.
 *
 * Pure, so the conflict panel and its test read the same list.
 */
export function nearestWindows(
  offered: EndpointResponse<typeof guest.listWindows> | undefined,
  windowStart: string | null,
  limit = 3,
): readonly DeliveryWindow[] {
  if (offered === undefined) return [];
  const from = windowStart === null ? Number.NaN : Date.parse(windowStart);
  const candidates: DeliveryWindow[] = [];
  for (const day of offered.serviceDays) {
    for (const offer of day.windows) {
      if (!offer.selectable) continue;
      if (offer.window.start === windowStart) continue;
      candidates.push(offer.window);
    }
  }
  const distance = (entry: DeliveryWindow): number =>
    Number.isNaN(from) ? Date.parse(entry.start) : Math.abs(Date.parse(entry.start) - from);
  return candidates
    .sort((a, b) => distance(a) - distance(b) || Date.parse(a.start) - Date.parse(b.start))
    .slice(0, limit);
}

/**
 * The products the picker must be answered about (RTE-008, GST-002).
 *
 * Per-product caps are part of a counter's capacity, so `GET /g/windows`
 * takes the basket's products and answers with the mornings that can actually
 * be filled. A SET is not one of those products: what gets baked is its
 * PARTS, and which parts they are is the card's answer plus the guest's -
 * every item of every `INCLUDED` section, and the option picked for each
 * `CHOICE` one (founder decision, 2026-08-30).
 *
 * Deduplicated, in basket order, because the server asks a capacity question
 * per product and the same croissant reaching it four times is four identical
 * questions.
 *
 * `items` is the shelf THIS screen has loaded, and it may be empty - the
 * picker reopened from the basket has none. A line whose card cannot be read
 * falls back to what the basket itself knows: the product on the line, and
 * the answers it carries. It says less rather than something wrong.
 */
export function windowsItemIdsOf(
  basket: BasketState,
  items: readonly CatalogueItem[],
): readonly string[] {
  const shelf = new Map(items.map((item) => [item.id, item]));
  const asked = new Set<string>();
  for (const line of basket.lines) {
    const card = shelf.get(line.itemId)?.formule;
    if (card === undefined || card.length === 0) {
      asked.add(line.itemId);
      for (const choice of line.choices ?? []) asked.add(choice.itemId);
      continue;
    }
    const answers = new Map((line.choices ?? []).map((choice) => [choice.componentRank, choice]));
    for (const bullet of [...card].sort((a, b) => a.rank - b.rank)) {
      if (bullet.kind === 'INCLUDED') {
        for (const choice of bullet.choices) asked.add(choice.itemId);
        continue;
      }
      const answer = answers.get(bullet.rank);
      if (answer !== undefined) asked.add(answer.itemId);
    }
  }
  return [...asked];
}
